import { useState, useCallback } from "react";
import type { PublicComboWithDetails } from "./usePublicCombos";

export interface SelectedAddon {
  id: string;
  name: string;
  price: number;
}

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  notes?: string;
  image_url?: string | null;
  selectedAddons?: SelectedAddon[];
}

// Combo selection types for public cart (using combo_slots system)
export interface PublicComboSelection {
  slotSelections?: Record<string, {
    productId: string;
    productName: string;
    price: number;
  }[]>;
  // Legacy support - can be removed after full migration
  selectedFlavors?: {
    productId: string;
    productName: string;
    price: number;
  }[];
  selectedChoices?: {
    comboItemId: string;
    choiceLabel: string;
    productId: string;
    productName: string;
  }[];
}

export interface ComboCartItem {
  comboId: string;
  comboName: string;
  comboType: string;
  price: number;
  quantity: number;
  notes?: string;
  image_url?: string | null;
  selection: PublicComboSelection;
}

export function usePublicCart() {
  const [items, setItems] = useState<CartItem[]>([]);
  const [comboItems, setComboItems] = useState<ComboCartItem[]>([]);

  const addItem = useCallback((
    product: {
      id: string;
      name: string;
      price: number;
      image_url?: string | null;
    },
    quantity: number = 1,
    notes?: string,
    selectedAddons?: SelectedAddon[]
  ) => {
    setItems((current) => {
      // Create a unique key for this combination of product + addons + notes
      const addonsKey = selectedAddons?.map(a => a.id).sort().join(",") || "";
      const notesKey = notes || "";
      
      const existingIndex = current.findIndex((item) => 
        item.id === product.id && 
        (item.selectedAddons?.map(a => a.id).sort().join(",") || "") === addonsKey &&
        (item.notes || "") === notesKey
      );
      
      if (existingIndex >= 0) {
        const updated = [...current];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
        };
        return updated;
      }
      
      // Calculate item price including addons
      const addonsTotal = selectedAddons?.reduce((sum, a) => sum + a.price, 0) || 0;
      
      return [...current, {
        id: product.id,
        name: product.name,
        price: product.price + addonsTotal,
        quantity,
        notes,
        image_url: product.image_url,
        selectedAddons,
      }];
    });
  }, []);

  const addComboItem = useCallback((
    combo: {
      id: string;
      name: string;
      combo_type: string;
      image_url?: string | null;
    },
    calculatedPrice: number,
    selection: PublicComboSelection,
    quantity: number = 1,
    notes?: string
  ) => {
    setComboItems((current) => {
      const selectionKey = JSON.stringify(selection);
      const existingIndex = current.findIndex(
        (item) => item.comboId === combo.id && JSON.stringify(item.selection) === selectionKey && item.notes === notes
      );

      if (existingIndex >= 0) {
        const updated = [...current];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
        };
        return updated;
      }

      return [...current, {
        comboId: combo.id,
        comboName: combo.name,
        comboType: combo.combo_type,
        price: calculatedPrice,
        quantity,
        notes,
        image_url: combo.image_url,
        selection,
      }];
    });
  }, []);

  const removeComboItem = useCallback((index: number) => {
    setComboItems((current) => current.filter((_, i) => i !== index));
  }, []);

  const updateComboQuantity = useCallback((index: number, quantity: number) => {
    if (quantity <= 0) {
      setComboItems((current) => current.filter((_, i) => i !== index));
      return;
    }
    setComboItems((current) =>
      current.map((item, i) =>
        i === index ? { ...item, quantity } : item
      )
    );
  }, []);

  const removeItem = useCallback((itemIndex: number) => {
    setItems((current) => current.filter((_, index) => index !== itemIndex));
  }, []);

  const removeItemById = useCallback((productId: string) => {
    setItems((current) => current.filter((item) => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((itemIndex: number, quantity: number) => {
    if (quantity <= 0) {
      removeItem(itemIndex);
      return;
    }
    
    setItems((current) =>
      current.map((item, index) =>
        index === itemIndex ? { ...item, quantity } : item
      )
    );
  }, [removeItem]);

  const updateQuantityById = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItemById(productId);
      return;
    }
    
    setItems((current) =>
      current.map((item) =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  }, [removeItemById]);

  const updateItemNotes = useCallback((index: number, notes: string) => {
    setItems((current) =>
      current.map((item, i) =>
        i === index ? { ...item, notes: notes || undefined } : item
      )
    );
  }, []);

  const updateComboNotes = useCallback((index: number, notes: string) => {
    setComboItems((current) =>
      current.map((item, i) =>
        i === index ? { ...item, notes: notes || undefined } : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setComboItems([]);
  }, []);

  const productSubtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const comboSubtotal = comboItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const subtotal = productSubtotal + comboSubtotal;
  
  const productItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const comboItemCount = comboItems.reduce((sum, item) => sum + item.quantity, 0);
  const itemCount = productItemCount + comboItemCount;

  return {
    items,
    comboItems,
    addItem,
    addComboItem,
    removeItem,
    removeItemById,
    removeComboItem,
    updateQuantity,
    updateQuantityById,
    updateComboQuantity,
    updateItemNotes,
    updateComboNotes,
    clearCart,
    subtotal,
    itemCount,
  };
}
